import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { Menu, X, ArrowUpRight, BookOpen, ChevronDown, LayoutGrid, LogOut, Settings } from "lucide-react";
import { scrollToId, scrollToTop, StatusDot } from "./primitives";
import { useAuth } from "@/auth/AuthContext";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const initials = (user) => {
    const src = user?.name || user?.email || "?";
    return src
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0].toUpperCase())
        .join("");
};

const LINKS = [
    { label: "Product", href: "#capabilities" },
    { label: "Command Center", href: "#command-center" },
    { label: "Developers", href: "#developers" },
    { label: "FAQ", href: "#faq" },
];

export const Logo = () => (
    <a
        href="#top"
        data-testid="nav-logo-link"
        onClick={(e) => {
            e.preventDefault();
            scrollToTop();
        }}
        className="group flex items-center gap-2.5"
    >
        <img
            src="/assets/logo-full.png"
            alt="MDH-API"
            className="h-6 w-auto transition-opacity group-hover:opacity-90"
        />
        <StatusDot className="ml-0.5" />
    </a>
);

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 28, mass: 0.4 });

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, href) => {
        e.preventDefault();
        setOpen(false);
        scrollToId(href);
    };

    return (
        <header
            data-testid="site-nav"
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
                scrolled
                    ? "border-b border-slate-800/80 bg-[#0B0F17]/70 backdrop-blur-md"
                    : "border-b border-transparent"
            }`}
        >
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
                <Logo />
                <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={(e) => go(e, l.href)}
                            className="text-xs font-medium tracking-wide text-slate-400 transition-colors hover:text-cyan-300"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>
                <div className="hidden items-center gap-3 lg:flex">
                    <Link
                        to="/docs"
                        data-testid="nav-read-docs-button"
                        className="flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-cyan-300"
                    >
                        <BookOpen className="h-3.5 w-3.5 text-slate-500" />
                        Read the Docs
                    </Link>
                    {loading ? null : user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                data-testid="nav-user-menu"
                                className="flex items-center gap-2.5 rounded-md border border-slate-800 bg-slate-900/50 py-1.5 pl-1.5 pr-2.5 outline-none transition-colors hover:border-cyan-500/40"
                            >
                                {user.picture ? (
                                    <img
                                        data-testid="nav-user-avatar"
                                        src={user.picture}
                                        alt={user.name || user.email}
                                        className="h-7 w-7 rounded-full border border-slate-700 object-cover"
                                        referrerPolicy="no-referrer"
                                    />
                                ) : (
                                    <span
                                        data-testid="nav-user-avatar"
                                        className="flex h-7 w-7 items-center justify-center rounded-full border border-cyan-500/40 bg-cyan-950/60 font-mono text-[10px] font-bold text-cyan-300"
                                    >
                                        {initials(user)}
                                    </span>
                                )}
                                <span
                                    data-testid="nav-user-name"
                                    className="max-w-[140px] truncate text-xs font-semibold text-slate-200"
                                >
                                    {user.name || user.email}
                                </span>
                                <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="w-52 border-slate-800 bg-[#0D1420] text-slate-200"
                            >
                                <DropdownMenuLabel className="font-mono text-[10px] font-normal uppercase tracking-[0.2em] text-slate-500">
                                    {user.email}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator className="bg-slate-800" />
                                <DropdownMenuItem
                                    data-testid="nav-menu-dashboard"
                                    onClick={() => navigate("/dashboard")}
                                    className="cursor-pointer gap-2 text-xs focus:bg-slate-800 focus:text-cyan-300"
                                >
                                    <LayoutGrid className="h-3.5 w-3.5" /> Dashboard
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    data-testid="nav-menu-settings"
                                    onClick={() => navigate("/settings")}
                                    className="cursor-pointer gap-2 text-xs focus:bg-slate-800 focus:text-cyan-300"
                                >
                                    <Settings className="h-3.5 w-3.5" /> Settings
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-slate-800" />
                                <DropdownMenuItem
                                    data-testid="nav-menu-logout"
                                    onClick={async () => {
                                        await logout();
                                        navigate("/");
                                    }}
                                    className="cursor-pointer gap-2 text-xs text-rose-300 focus:bg-rose-950/40 focus:text-rose-200"
                                >
                                    <LogOut className="h-3.5 w-3.5" /> Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                data-testid="nav-login-button"
                                className="rounded-md px-3 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-cyan-300"
                            >
                                Log in
                            </Link>
                            <a
                                href="https://mdhapi.com/signup"
                                data-testid="nav-start-building-button"
                                className="group flex items-center gap-1.5 rounded-md bg-cyan-400 px-4 py-2 text-xs font-bold text-slate-950 shadow-[0_0_18px_rgba(6,182,212,0.35)] transition-all hover:bg-cyan-300 hover:shadow-[0_0_26px_rgba(6,182,212,0.55)]"
                            >
                                Start Building
                                <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                        </>
                    )}
                </div>
                <button
                    data-testid="nav-mobile-menu-button"
                    className="rounded-md border border-slate-800 p-2 text-slate-300 lg:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                </button>
            </div>
            <motion.div
                data-testid="scroll-progress-rail"
                style={{ scaleX }}
                className="absolute -bottom-px left-0 h-0.5 w-full origin-left bg-gradient-to-r from-cyan-500 via-cyan-400 to-teal-400"
            />
            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="border-t border-slate-800/80 bg-[#0B0F17]/95 px-5 py-4 backdrop-blur-xl lg:hidden"
                >
                    <div className="flex flex-col gap-1">
                        {LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={(e) => go(e, l.href)}
                                className="rounded-md px-2 py-2.5 text-sm text-slate-300 hover:bg-slate-900"
                            >
                                {l.label}
                            </a>
                        ))}
                        <Link
                            to="/docs"
                            data-testid="nav-mobile-link-docs"
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-2 rounded-md px-2 py-2.5 text-sm text-slate-300 hover:bg-slate-900"
                        >
                            <BookOpen className="h-3.5 w-3.5 text-slate-500" />
                            Read the Docs
                        </Link>
                        {user ? (
                            <Link
                                to="/dashboard"
                                data-testid="nav-mobile-dashboard"
                                onClick={() => setOpen(false)}
                                className="rounded-md px-2 py-2.5 text-sm font-semibold text-cyan-300 hover:bg-slate-900"
                            >
                                Dashboard
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    data-testid="nav-mobile-login"
                                    onClick={() => setOpen(false)}
                                    className="mt-2 rounded-md border border-slate-700 px-4 py-2.5 text-center text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-500/50 hover:text-cyan-300"
                                >
                                    Log in
                                </Link>
                                <a
                                    href="https://mdhapi.com/signup"
                                    data-testid="nav-mobile-start-building-button"
                                    onClick={() => setOpen(false)}
                                    className="mt-2 rounded-md bg-cyan-400 px-4 py-2.5 text-center text-sm font-bold text-slate-950"
                                >
                                    Start Building
                                </a>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
